export type Permission = string;
/**
 * Checks if a user permission matches a required permission.
 * Supports wildcards in the format "resource:*" or "admin:all"
 *
 * Examples:
 * - "admin:all" matches everything
 * - "*" matches everything
 * - "students:*" matches "students:read", "students:write", etc.
 * - "students:read" matches only "students:read"
 *
 * @param granted - The permission the user has
 * @param required - The permission being checked
 */
export const matchesPermission = (granted: Permission, required: Permission): boolean => {
  // Exact match
  if (granted === required) {
    return true;
  }

  // Super admin wildcards
  if (granted === '*' || granted === 'admin:all') {
    return true;
  }

  // Resource wildcard: "students:*" matches "students:read", "students:write", etc.
  if (granted.endsWith(':*')) {
    const grantedResource = granted.slice(0, -2); // Remove ":*"
    const requiredResource = required.split(':')[0];
    return grantedResource === requiredResource;
  }

  return false;
};

/**
 * Checks if a user has a required permission given their granted permissions.
 *
 * @param grantedPermissions - Array of permissions the user has
 * @param requiredPermission - The permission being checked
 * @returns true if any of the granted permissions match the required permission
 */
export const hasPermission = (grantedPermissions: Permission[], requiredPermission: Permission): boolean => {
  if (!requiredPermission) {
    return false;
  }

  // Check if any of the user's permissions match the required permission
  for (const granted of grantedPermissions) {
    if (matchesPermission(granted, requiredPermission)) {
      return true;
    }
  }

  return false;
};